import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, RefreshCw, TrendingUp, Store } from "lucide-react";

const FIXED_ROWS = [
  ["rent", "Premises Rent"],
  ["electricity", "Premises Electricity"],
  ["staff_salary", "Staff Salary"],
  ["staff_room_rent", "Staff Room Rent"],
  ["staff_food", "Staff Food"],
  ["maintenance", "Maintenance Expenses/Gas"],
  ["housekeeping", "Housekeeping Cost"],
  ["marketing", "Marketing Cost"],
  ["misc", "Other / Miscellaneous Expenses"],
  ["manpower_charges", "Manpower Supplier Charges"],
];

const ZERO = {
  daily_sales: 0, operating_days: 30, capex: 0,
  rent: 0, electricity: 0, staff_salary: 0, staff_room_rent: 0, staff_food: 0,
  maintenance: 0, housekeeping: 0, marketing: 0, misc: 0, manpower_charges: 0,
  food_pct: 35, royalty_pct: 5,
};

const inr = (n, d = 0) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: 2 })}`;

function compute(inp) {
  const gross = (inp.daily_sales || 0) * (inp.operating_days || 30);
  const food = gross * (inp.food_pct / 100);
  const royalty = gross * (inp.royalty_pct / 100);
  const fixed = FIXED_ROWS.reduce((s, [k]) => s + (Number(inp[k]) || 0), 0);
  const totalCost = food + royalty + fixed;
  const net = gross - totalCost;
  const margin = gross ? (net / gross) * 100 : 0;
  const annualGross = gross * 12, annualNet = net * 12;
  const capex = Number(inp.capex) || 0;
  const roiPct = capex ? (annualNet / capex) * 100 : 0;
  const roiYears = annualNet > 0 ? capex / annualNet : 0;
  const roiMonths = net > 0 ? capex / net : 0;
  const factor = 1 - (inp.food_pct + inp.royalty_pct) / 100;
  const beMonthly = factor > 0 ? fixed / factor : 0;
  return { gross, food, royalty, fixed, totalCost, net, margin, annualGross, annualNet,
           roiPct, roiYears, roiMonths, beMonthly, beDaily: beMonthly / (inp.operating_days || 30) };
}

const SecHead = ({ children }) => (
  <div className="text-center font-mono2 text-xs tracking-[0.25em] uppercase py-2.5 rounded-lg mt-6 mb-0"
    style={{ background: "#3B2519", color: "#F5C242" }}>{children}</div>
);

const Row = ({ label, children, note, strong, gold }) => (
  <div className={`grid grid-cols-[1fr_auto] md:grid-cols-[1fr_180px_1fr] items-center border-b ${gold ? "font-bold" : ""}`}
    style={{ borderColor: "#E4D6A8", background: gold ? "#F5C242" : "transparent" }}>
    <div className={`px-3 py-2 text-sm ${strong ? "font-bold" : ""}`} style={{ color: gold ? "#3B2519" : "#2b1a0d" }}>{label}</div>
    <div className="px-3 py-1.5">{children}</div>
    <div className="hidden md:block px-3 py-2 text-xs" style={{ color: "#7a6349" }}>{note || ""}</div>
  </div>
);

const MoneyCell = ({ value, onChange, testid, disabled }) => (
  <Input data-testid={testid} type="number" min="0" step="any" value={value}
    onChange={e => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    disabled={disabled}
    className="h-8 text-right font-mono2 border-[#E4D6A8]"
    style={{ background: disabled ? "#F5EFE0" : "#FFF3C4" }} />
);

const ResultCell = ({ children, strong }) => (
  <div className={`px-2 py-1 text-right font-mono2 text-sm ${strong ? "font-bold" : ""}`}
    style={{ color: "#2b1a0d", background: "#F5EFE0", borderRadius: 6 }}>{children}</div>
);

export default function ROICalculator() {
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [inp, setInp] = useState(ZERO);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  useEffect(() => {
    api.get("/ops/outlets").then(r => {
      setOutlets(r.data);
      if (r.data.length && !outletId) setOutletId(r.data[0].id);
    }).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!outletId) return;
    api.get(`/roi/${outletId}`).then(r => {
      setInp({ ...ZERO, ...r.data.inputs });
      setMeta({ updated_by: r.data.updated_by, updated_at: r.data.updated_at });
      setCanEdit(true);
    }).catch(e => {
      if (e.response?.status === 403) { setCanEdit(false); setInp(ZERO); }
      toast.error(e.response?.data?.detail || "Load failed");
    });
  }, [outletId]);

  const R = useMemo(() => compute(inp), [inp]);
  const set = (k, v) => setInp(s => ({ ...s, [k]: v }));
  const outletName = outlets.find(o => o.id === outletId)?.name || "";

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/roi/${outletId}`, inp);
      toast.success(`ROI saved for ${outletName}`);
      setMeta({ updated_by: "you", updated_at: new Date().toISOString() });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed (view-only access)"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="roi-root" className="rounded-2xl border overflow-hidden" style={{ borderColor: "#E4D6A8", background: "#FFFDF6" }}>
      {/* Header */}
      <div className="py-3 text-center" style={{ background: "#F5C242" }}>
        <div className="font-display text-2xl tracking-wide" style={{ color: "#3B2519" }}>CHAI CHASKA CAFÉ</div>
      </div>
      <div className="py-2 text-center font-mono2 text-sm tracking-[0.2em] uppercase" style={{ background: "#3B2519", color: "#FFFDF6" }}>
        Outlet Profitability &amp; ROI Calculator
      </div>
      <div className="text-center text-xs italic py-1.5" style={{ background: "#FFF8E7", color: "#7a6349" }}>
        Enter outlet-specific figures in the yellow cells. GST is excluded from sales input.
      </div>

      {/* Outlet picker */}
      <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: "#E4D6A8", background: "#fff" }}>
        <Store size={16} style={{ color: "#B4841F" }} />
        <label className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>Outlet</label>
        <select data-testid="roi-outlet" value={outletId} onChange={e => setOutletId(e.target.value)}
          className="h-9 px-3 rounded-lg border bg-white text-sm min-w-[200px]" style={{ borderColor: "#E4D6A8" }}>
          {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {meta?.updated_at && (
          <span className="text-[10px] font-mono2" style={{ color: "#7a6349" }}>
            Last saved {meta.updated_at.slice(0, 10)} {meta.updated_by ? `by ${meta.updated_by}` : ""}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <Button data-testid="roi-reload" size="sm" variant="outline" onClick={() => setOutletId(id => id && id)}>
            <RefreshCw size={13} className="mr-1" /> Reload
          </Button>
          <Button data-testid="roi-save" size="sm" onClick={save} disabled={busy || !canEdit}
            className="bg-[#3B2519] text-[#F5C242] hover:bg-black">
            <Save size={13} className="mr-1" /> {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="p-4">
        {/* 1 · OUTLET INPUTS */}
        <SecHead>1 · Outlet Inputs</SecHead>
        <Row label="Average Daily Counter Sales – Excluding GST" note="Enter average daily counter sales before GST">
          <MoneyCell testid="roi-daily-sales" value={inp.daily_sales} onChange={v => set("daily_sales", v)} disabled={!canEdit} />
        </Row>
        <Row label="Operating Days per Month" note="Enter actual operating days">
          <MoneyCell testid="roi-days" value={inp.operating_days} onChange={v => set("operating_days", Math.max(1, Math.round(v)))} disabled={!canEdit} />
        </Row>
        <Row label="#Capex (Total Investment)" note="Enter total investment made in the outlet">
          <MoneyCell testid="roi-capex" value={inp.capex} onChange={v => set("capex", v)} disabled={!canEdit} />
        </Row>

        {/* 2 · FIXED EXPENSES */}
        <SecHead>2 · Monthly Fixed Expenses — Enter Your Figures</SecHead>
        {FIXED_ROWS.map(([k, label]) => (
          <Row key={k} label={label}>
            <MoneyCell testid={`roi-${k}`} value={inp[k]} onChange={v => set(k, v)} disabled={!canEdit} />
          </Row>
        ))}
        <Row label="TOTAL FIXED EXPENSES" strong gold>
          <ResultCell strong>{inr(R.fixed)}</ResultCell>
        </Row>

        {/* 3 · ASSUMPTIONS */}
        <SecHead>3 · Fixed Business Assumptions</SecHead>
        <Row label="Food / Raw Material Cost" note="% of Gross Sales">
          <div className="flex items-center gap-1">
            <Input data-testid="roi-food-pct" type="number" step="0.5" value={inp.food_pct} disabled={!canEdit}
              onChange={e => set("food_pct", Number(e.target.value) || 0)} className="h-8 w-20 text-right font-mono2 border-[#E4D6A8]" style={{ background: "#F5EFE0" }} />
            <span className="text-xs font-mono2" style={{ color: "#7a6349" }}>% FIXED</span>
          </div>
        </Row>
        <Row label="Royalty on Gross Sales" note="% of Gross Sales">
          <div className="flex items-center gap-1">
            <Input data-testid="roi-royalty-pct" type="number" step="0.5" value={inp.royalty_pct} disabled={!canEdit}
              onChange={e => set("royalty_pct", Number(e.target.value) || 0)} className="h-8 w-20 text-right font-mono2 border-[#E4D6A8]" style={{ background: "#F5EFE0" }} />
            <span className="text-xs font-mono2" style={{ color: "#7a6349" }}>% FIXED</span>
          </div>
        </Row>

        {/* 4 · RESULTS */}
        <SecHead>4 · Automatic Profitability &amp; ROI Results</SecHead>
        <Row label="Monthly Gross Sales"><ResultCell>{inr(R.gross)}</ResultCell></Row>
        <Row label={`Food / Raw Material Cost – ${inp.food_pct}%`}><ResultCell>{inr(R.food)}</ResultCell></Row>
        <Row label={`Royalty – ${inp.royalty_pct}% of Gross Sales`}><ResultCell>{inr(R.royalty)}</ResultCell></Row>
        <Row label="Total Fixed Expenses"><ResultCell>{inr(R.fixed)}</ResultCell></Row>
        <Row label="TOTAL MONTHLY COST" strong gold><ResultCell strong>{inr(R.totalCost)}</ResultCell></Row>
        <Row label="NET PROFIT – MONTHLY" strong gold><ResultCell strong>{inr(R.net)}</ResultCell></Row>
        <Row label="NET PROFIT MARGIN %" strong gold>
          <ResultCell strong><span style={{ color: R.margin >= 0 ? "#0a7a3b" : "#c0392b" }}>{R.margin.toFixed(2)}%</span></ResultCell>
        </Row>
        <Row label="Annual Gross Sales"><ResultCell>{inr(R.annualGross)}</ResultCell></Row>
        <Row label="Annual Net Profit"><ResultCell>{inr(R.annualNet)}</ResultCell></Row>
        <Row label="ANNUAL ROI %" strong gold><ResultCell strong>{R.roiPct.toFixed(2)}%</ResultCell></Row>
        <Row label="ROI PERIOD – YEARS" strong gold><ResultCell strong>{R.roiYears.toFixed(2)}</ResultCell></Row>
        <Row label="ROI PERIOD – MONTHS" strong gold><ResultCell strong>{R.roiMonths.toFixed(2)}</ResultCell></Row>
        <Row label="Break-Even Sales – Monthly"><ResultCell>{inr(R.beMonthly)}</ResultCell></Row>
        <Row label="Break-Even Sales – Daily"><ResultCell>{inr(R.beDaily)}</ResultCell></Row>

        {/* Calculation logic */}
        <SecHead>Calculation Logic</SecHead>
        <p className="text-xs px-3 py-3" style={{ color: "#7a6349" }}>
          Gross Sales = Daily Counter Sales × Operating Days. Food Cost = {inp.food_pct}% of Gross Sales. Royalty = {inp.royalty_pct}% of Gross Sales.
          Net Profit = Gross Sales − Food Cost − Royalty − Fixed Expenses. Annual ROI = Annual Net Profit ÷ Total Investment.
          ROI Period = Total Investment ÷ Annual Net Profit. Break-even = Fixed ÷ (1 − {(inp.food_pct + inp.royalty_pct) / 100}).
        </p>
        <p className="text-xs px-3 pb-3 font-semibold" style={{ color: "#B4841F" }}>
          IMPORTANT: Sales input must be EXCLUDING 5% GST. Food Cost is fixed at {inp.food_pct}% and Royalty at {inp.royalty_pct}% of gross sales.
        </p>
      </div>
    </div>
  );
}
